import {
  useCallback, useEffect, useRef, useState
} from 'react';
import { captureException } from '@sentry/react';

import { noop } from '@/utils';

interface ApiError {
  response?: {
    data?: Record<string, unknown> | null;
  };
}

interface UseApiCallResult {
  apiErrors: Record<string, unknown> | null;
  processing: boolean;
  callback: (event?: Event | React.SyntheticEvent) => Promise<unknown>;
}

const useApiCall = <TResult = unknown>(
  onSend: () => unknown = noop,
  onSuccess: (result: TResult) => unknown = noop,
  onFail: (error: unknown) => unknown = noop,
  onValidate: () => unknown = noop
): UseApiCallResult => {
  const mountedRef = useRef(false);

  const [processing, setProcessing] = useState(false);
  const [apiErrors, setApiErrors] = useState<Record<string, unknown> | null>(null);

  const callback = useCallback(async(event?: Event | React.SyntheticEvent) => {
    if (event && 'preventDefault' in event) {
      event.preventDefault();
    }

    if (processing) {
      return undefined;
    }

    const validate = onValidate();

    if (validate) {
      return undefined;
    }

    setProcessing(true);
    setApiErrors(null);

    const promise = onSend();

    // sync api call
    if (!(promise instanceof Promise)) {
      if (mountedRef.current) {
        setProcessing(false);
      }

      return onSuccess(promise as TResult);
    }

    // async api call
    try {
      const action = await promise;
      return onSuccess(action as TResult);
    } catch (err) {
      const error = err as ApiError;

      if (mountedRef.current && error?.response) {
        setApiErrors(error.response.data || null);
      }

      if (!error?.response) {
        captureException(err);
      }

      return onFail(err);
    } finally {
      if (mountedRef.current) {
        setProcessing(false);
      }
    }
  }, [
    mountedRef,
    processing,
    onFail,
    onSend,
    onSuccess,
    onValidate
  ]);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, [mountedRef]);

  return {
    apiErrors,
    processing,
    callback
  };
};

export default useApiCall;
